import React from "react";
import { usePublicData } from "../lib/publicData";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Quote } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";
import { GoogleReviewButton } from '../components/SiteFeatures';

function formatMonth(iso) {
  if (!iso) return "";
  const months = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

export default function Avis() {
  const initial = usePublicData();
  const [items, setItems] = React.useState(initial.testimonials || []);
  const [loading, setLoading] = React.useState(!initial.testimonials);

  React.useEffect(() => {
    api.get("/testimonials")
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const total = items.length;
  const average = total > 0 ? (items.reduce((s, t) => s + (t.note || 5), 0) / total).toFixed(1) : "5.0";

  return (
    <div className="section-dark min-h-screen pt-28 pb-20 relative" data-testid="avis-page">
      <Grain />
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow mb-4">Témoignages clients</div>
        <h1 className="font-serif text-5xl md:text-6xl mb-6" data-testid="avis-title">
          Ce qu'ils en <span className="italic-accent">disent</span>
        </h1>
        <div className="flex items-center gap-4 mb-14 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-accent-deep stroke-accent-deep" />
              ))}
            </div>
            <span className="font-serif text-2xl text-accent-light">{average}</span>
            <span className="text-text-cream/50 text-sm font-light">/ 5</span>
          </div>
          <span className="text-text-cream/40 text-sm font-light">
            {total} avis vérifiés · Heygoldie, Google & client direct
          </span>
        </div>

        {loading && (
          <div className="text-text-cream/60 font-light text-sm" data-testid="avis-loading">
            Chargement…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-text-cream/60 font-light italic text-sm">Aucun témoignage pour le moment.</div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="avis-grid">
          {items.map((t) => (
            <article
              key={t.id}
              className="relative bg-white/[0.03] border border-white/10 rounded p-6 md:p-7 flex flex-col"
              data-testid={`avis-card-${t.id}`}
            >
              <Quote
                size={28}
                className="absolute top-5 right-5 text-accent-deep/20"
                strokeWidth={1.2}
              />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.note || 5 }).map((_, i) => (
                  <Star key={i} size={13} className="fill-accent-deep stroke-accent-deep" />
                ))}
              </div>
              <p className="font-serif italic font-light text-text-cream/90 leading-relaxed text-base md:text-lg flex-1">
                « {t.texte} »
              </p>
              <div className="mt-6 pt-4 border-t border-white/10 flex items-baseline justify-between gap-4">
                <div>
                  <div className="font-serif text-text-cream text-base">
                    {t.prenom} {t.nom_initial || ""}
                  </div>
                  {t.service && (
                    <div className="text-text-cream/40 text-xs font-light mt-0.5">{t.service}</div>
                  )}
                </div>
                <div className="text-text-cream/40 text-[0.65rem] uppercase tracking-wider text-right shrink-0">
                  {t.source && (t.review_uri
                    ? <a href={t.review_uri} target="_blank" rel="noopener noreferrer" className="text-accent-deep/70 hover:text-accent-light underline" data-testid={`avis-google-link-${t.id}`}>Avis {t.source}</a>
                    : <div className="text-accent-deep/70">{t.source}</div>)}
                  <div>{formatMonth(t.created_at)}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 bg-accent-deep/10 border border-accent-deep/30 rounded p-8 md:p-10 text-center">
          <h2 className="font-serif text-3xl text-accent-light mb-3">
            Tu as fait une séance avec moi ?
          </h2>
          <p className="text-text-cream/70 font-light mb-6 max-w-xl mx-auto">
            Partage ton expérience sur Google — ça aide d'autres personnes à franchir le cap et me permet
            de continuer à faire ce métier avec exigence.
          </p>
          <GoogleReviewButton id="avis"/>
          <div className="mt-8">
            <Link
              to="/reservation"
              className="text-text-cream/50 hover:text-accent-light text-sm transition inline-flex items-center gap-2"
              data-testid="avis-book-link"
            >
              Prêt·e à réserver ta séance <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
